import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import productService from '../services/productService';
import ProductsTable from '../components/ProductsTable';
import '../styles/products.css';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (search.trim()) params.search = search.trim();
      if (status !== 'all') params.is_active = status;

      const result = await productService.getProducts(params);

      setProducts(result.products || []);
      setPagination((previous) => ({
        ...previous,
        ...(result.pagination || {}),
      }));
    } catch (err) {
      setError(
        err.response?.data?.message || 'Unable to load products.'
      );
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, status]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSearch = (event) => {
    event.preventDefault();
    setPagination((previous) => ({ ...previous, page: 1 }));
    setSearch(searchInput);
  };

  const handleStatusFilter = (event) => {
    setStatus(event.target.value);
    setPagination((previous) => ({ ...previous, page: 1 }));
  };

  const handleStatusChange = async (product) => {
    const nextStatus = !product.is_active;
    const confirmed = window.confirm(
      `${nextStatus ? 'Activate' : 'Deactivate'} "${product.product_name}"?`
    );

    if (!confirmed) return;

    setUpdatingId(product.id);
    setError('');
    setNotice('');

    try {
      await productService.updateProductStatus(
        product.id,
        nextStatus
      );

      setNotice(
        `Product ${nextStatus ? 'activated' : 'deactivated'} successfully.`
      );

      await loadProducts();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Unable to update product status.'
      );
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <section className="products-page">
      <div className="products-page-header">
        <div>
          <h1>Products</h1>
          <p>Manage the products belonging to your firm.</p>
        </div>

        <Link
          to="/user/products/create"
          className="product-primary-button"
        >
          + Add Product
        </Link>
      </div>

      {error && (
        <div className="product-alert product-alert-error">
          {error}
        </div>
      )}

      {notice && (
        <div className="product-alert product-alert-success">
          {notice}
        </div>
      )}

      <form className="products-toolbar" onSubmit={handleSearch}>
        <input
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search product, SKU, or brand..."
          aria-label="Search products"
        />

        <button
          type="submit"
          className="product-secondary-button"
        >
          Search
        </button>

        <select
          value={status}
          onChange={handleStatusFilter}
          aria-label="Filter by product status"
        >
          <option value="all">All products</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
      </form>

      <div className="products-table-card">
        <div className="products-table-heading">
          <h2>Product List</h2>
          <span>{pagination.total} total</span>
        </div>

        <ProductsTable
          products={products.map((product) => ({
            ...product,
            statusUpdating: updatingId === product.id,
          }))}
          loading={loading}
          onStatusChange={handleStatusChange}
        />

        <div className="products-pagination">
          <span>
            Page {pagination.page} of {Math.max(pagination.totalPages, 1)}
          </span>

          <div>
            <button
              type="button"
              disabled={loading || pagination.page <= 1}
              onClick={() =>
                setPagination((previous) => ({
                  ...previous,
                  page: previous.page - 1,
                }))
              }
            >
              Previous
            </button>

            <button
              type="button"
              disabled={
                loading ||
                pagination.page >= pagination.totalPages
              }
              onClick={() =>
                setPagination((previous) => ({
                  ...previous,
                  page: previous.page + 1,
                }))
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsPage;